import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { formatDate, type SupportedLocale } from "@/lib/datetime"
import { formatCount } from "@/lib/format"
import { tickIndices, totals, type TrafficDay } from "../lib/traffic"

const WIDTH = 720
const HEIGHT = 240
const PAD = { top: 12, right: 12, bottom: 28, left: 12 }
const PLOT_W = WIDTH - PAD.left - PAD.right
const PLOT_H = HEIGHT - PAD.top - PAD.bottom

/** A bucket is a UTC day (backend `$dateTrunc`, timezone UTC): label it by that date. */
const dayLabel = (start: string, locale: SupportedLocale) =>
  formatDate(`${start.slice(0, 10)}T00:00`, locale)

/**
 * FR-DSH-OVW-04, adapted from akav-eli's `RevenueChart`: sent and received
 * per day in plain SVG (no chart library), the previous window dashed and
 * aligned by day offset, a crosshair reading the day under the pointer,
 * and the same numbers as a table. Colours come only from `--chart-1/2`.
 */
export function TrafficChart({
  days,
  locale,
  from,
  to,
}: {
  days: TrafficDay[]
  locale: SupportedLocale
  /** The window, for the summary label: `to` exclusive. */
  from: string
  to: string
}) {
  const { t } = useTranslation("overview")
  const [view, setView] = useState<"chart" | "table">("chart")
  const [hover, setHover] = useState<number | null>(null)
  const sum = totals(days)

  if (
    sum.sent === 0 &&
    sum.received === 0 &&
    days.every((d) => !d.previous?.sent && !d.previous?.received)
  ) {
    return <p className="text-sm text-muted-foreground">{t("traffic.empty")}</p>
  }

  const last = new Date(Date.parse(to) - 1).toISOString()
  const label = t("traffic.label", {
    sent: formatCount(sum.sent, locale),
    received: formatCount(sum.received, locale),
    from: dayLabel(from, locale),
    to: dayLabel(last, locale),
  })

  const toggle = (
    <div role="group" aria-label={t("traffic.view")} className="flex gap-1">
      {(["chart", "table"] as const).map((v) => (
        <Button
          key={v}
          type="button"
          size="sm"
          variant={view === v ? "secondary" : "ghost"}
          aria-pressed={view === v}
          onClick={() => {
            setView(v)
          }}
        >
          {t(`traffic.${v}`)}
        </Button>
      ))}
    </div>
  )

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Legend />
        {toggle}
      </div>
      {view === "chart" ? (
        <Plot days={days} label={label} locale={locale} hover={hover} onHover={setHover} />
      ) : (
        <TrafficTable days={days} locale={locale} caption={label} />
      )}
    </div>
  )
}

function Legend() {
  const { t } = useTranslation("overview")
  const swatch = (cls: string, dashed = false) => (
    <span
      aria-hidden
      className={`inline-block h-0.5 w-4 ${dashed ? "border-t-2 border-dashed border-muted-foreground" : cls}`}
    />
  )
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <li className="flex items-center gap-1.5">
        {swatch("bg-chart-1")}
        {t("traffic.sent")}
      </li>
      <li className="flex items-center gap-1.5">
        {swatch("bg-chart-2")}
        {t("traffic.received")}
      </li>
      <li className="flex items-center gap-1.5">
        {swatch("", true)}
        {t("traffic.previous")}
      </li>
    </ul>
  )
}

function Plot({
  days,
  label,
  locale,
  hover,
  onHover,
}: {
  days: TrafficDay[]
  label: string
  locale: SupportedLocale
  hover: number | null
  onHover: (i: number | null) => void
}) {
  const { t } = useTranslation("overview")
  const values = days.flatMap((d) => [
    d.sent,
    d.received,
    d.previous?.sent ?? 0,
    d.previous?.received ?? 0,
  ])
  const max = Math.max(1, ...values)
  const step = days.length > 1 ? PLOT_W / (days.length - 1) : 0
  const x = (i: number) => PAD.left + (days.length > 1 ? i * step : PLOT_W / 2)
  const y = (v: number) => PAD.top + PLOT_H - (v / max) * PLOT_H
  const line = (pick: (d: TrafficDay) => number | undefined) =>
    days
      .map((d, i) => [i, pick(d)] as const)
      .filter((p): p is readonly [number, number] => p[1] !== undefined)
      .map(([i, v], k) => `${k === 0 ? "M" : "L"}${String(x(i))},${String(y(v))}`)
      .join(" ")
  const hovered = hover === null ? undefined : days[hover]

  return (
    <div>
      <svg
        viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
        className="w-full touch-none"
        role="img"
        aria-label={label}
        onMouseLeave={() => {
          onHover(null)
        }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          if (rect.width === 0) return
          const at = ((e.clientX - rect.left) / rect.width) * WIDTH
          const i = step > 0 ? Math.round((at - PAD.left) / step) : 0
          onHover(Math.min(Math.max(i, 0), days.length - 1))
        }}
      >
        <line
          x1={PAD.left}
          y1={PAD.top + PLOT_H}
          x2={WIDTH - PAD.right}
          y2={PAD.top + PLOT_H}
          stroke="var(--border)"
        />
        <path
          d={line((d) => d.previous?.sent)}
          fill="none"
          stroke="var(--chart-1)"
          strokeOpacity={0.5}
          strokeWidth={1.5}
          strokeDasharray="5 4"
        />
        <path
          d={line((d) => d.previous?.received)}
          fill="none"
          stroke="var(--chart-2)"
          strokeOpacity={0.5}
          strokeWidth={1.5}
          strokeDasharray="5 4"
        />
        <path d={line((d) => d.sent)} fill="none" stroke="var(--chart-1)" strokeWidth={2} />
        <path d={line((d) => d.received)} fill="none" stroke="var(--chart-2)" strokeWidth={2} />
        {tickIndices(days.length).map((i) => {
          const d = days[i]
          return d ? (
            <text
              key={d.start}
              x={x(i)}
              y={HEIGHT - 8}
              textAnchor={i === 0 ? "start" : i === days.length - 1 ? "end" : "middle"}
              fontSize={11}
              className="fill-muted-foreground"
            >
              {dayLabel(d.start, locale).replace(/\s\d{4}$/, "")}
            </text>
          ) : null
        })}
        {hover !== null ? (
          <line
            x1={x(hover)}
            y1={PAD.top}
            x2={x(hover)}
            y2={PAD.top + PLOT_H}
            stroke="var(--muted-foreground)"
            strokeDasharray="2 2"
          />
        ) : null}
      </svg>
      {hovered ? (
        <div
          data-testid="traffic-readout"
          className="mt-2 rounded-md border border-border bg-popover p-2 text-xs"
        >
          <p className="font-medium">{dayLabel(hovered.start, locale)}</p>
          <p className="numeric">
            {t("traffic.sent")} {formatCount(hovered.sent, locale)} · {t("traffic.received")}{" "}
            {formatCount(hovered.received, locale)}
          </p>
          {hovered.previous ? (
            <p className="numeric text-muted-foreground">
              {t("traffic.previousDay", {
                sent: formatCount(hovered.previous.sent, locale),
                received: formatCount(hovered.previous.received, locale),
              })}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** The chart's numbers as a table: for screen readers and for anyone who wants them exact. */
function TrafficTable({
  days,
  locale,
  caption,
}: {
  days: TrafficDay[]
  locale: SupportedLocale
  caption: string
}) {
  const { t } = useTranslation("overview")
  const n = (v: number | undefined) => (v === undefined ? "—" : formatCount(v, locale))
  return (
    <div className="max-h-80 overflow-auto rounded-md border border-border">
      <table className="w-full text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="sticky top-0 bg-muted text-left text-xs text-muted-foreground">
          <tr>
            <th scope="col" className="px-3 py-2 font-medium">
              {t("traffic.day")}
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              {t("traffic.sent")}
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              {t("traffic.received")}
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              {t("traffic.previousSent")}
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              {t("traffic.previousReceived")}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {days.map((d) => (
            <tr key={d.start}>
              <th scope="row" className="px-3 py-1.5 text-left font-normal whitespace-nowrap">
                {dayLabel(d.start, locale)}
              </th>
              <td className="numeric px-3 py-1.5 text-right">{n(d.sent)}</td>
              <td className="numeric px-3 py-1.5 text-right">{n(d.received)}</td>
              <td className="numeric px-3 py-1.5 text-right text-muted-foreground">
                {n(d.previous?.sent)}
              </td>
              <td className="numeric px-3 py-1.5 text-right text-muted-foreground">
                {n(d.previous?.received)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
